import { z } from 'zod';
// Mirrors the versioned same-user Nyx Link IPC contract. No arbitrary URL, path, command or MCP method.
export const NYX_VERSION = 1;
export const NYX_MAX_BYTES = 24 * 1024;
export const clientSchema = z.enum(['codex', 'claude', 'cursor']);
export const modeSchema = z.enum(['ask_me', 'plan_only', 'auto_approve']);
const id = z.uuid();
const text = (max) => z
    .string()
    .min(1)
    .max(max)
    .refine((v) => new TextEncoder().encode(v).length <= max && !/\p{Cc}|[\ud800-\udfff]/u.test(v))
    .meta({
    description: `Nonempty well-formed Unicode identifier; at most ${max} UTF-8 bytes; no Unicode control characters.`,
    'x-maxUtf8Bytes': max,
});
const project = text(120).regex(/^[a-z0-9._/-]+$/);
const model = text(120).regex(/^[a-zA-Z0-9._:/][a-zA-Z0-9._:/-]*$/);
const nullable = (value) => value.nullable().optional();
export const answerSchema = z
    .object({ questionId: text(100), selectedOptionIds: z.array(text(100)).min(1).max(8) })
    .strict();
export const operationSchema = z.discriminatedUnion('method', [
    z.object({ method: z.literal('status') }).strict(),
    z.object({ method: z.literal('projects') }).strict(),
    z
        .object({ method: z.literal('history'), projectKey: project, cursor: nullable(text(512)) })
        .strict(),
    z.object({ method: z.literal('summary'), projectKey: project, sessionId: text(200) }).strict(),
    z.object({ method: z.literal('models'), client: clientSchema }).strict(),
    z.object({ method: z.literal('tasks') }).strict(),
    z
        .object({
        method: z.literal('events'),
        taskId: id,
        after: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
    })
        .strict(),
    z
        .object({
        method: z.literal('start'),
        projectKey: project,
        client: clientSchema,
        mode: modeSchema,
        model: nullable(model),
        effort: nullable(text(20).regex(/^[a-z]+$/)),
    })
        .strict(),
    z
        .object({
        method: z.literal('send'),
        taskId: id,
        text: z
            .string()
            .min(1)
            .max(16000)
            .refine((v) => !!v.trim() && !v.includes('\0') && new TextEncoder().encode(v).length <= 16000),
    })
        .strict(),
    z
        .object({ method: z.literal('answer'), taskId: id, requestId: text(200), approve: z.boolean() })
        .strict(),
    z
        .object({
        method: z.literal('answer_question'),
        taskId: id,
        requestId: text(200),
        answers: z.array(answerSchema).min(1).max(4),
    })
        .strict(),
    z.object({ method: z.literal('set_mode'), taskId: id, mode: modeSchema }).strict(),
    z.object({ method: z.literal('interrupt'), taskId: id }).strict(),
    z.object({ method: z.literal('stop'), taskId: id }).strict(),
]);
export const requestSchema = z
    .object({
    version: z.literal(1),
    id,
    issuedAt: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    operation: operationSchema,
})
    .strict();
export const responseSchema = z
    .object({
    version: z.literal(1),
    id,
    result: z.unknown().nullable(),
    error: z.string().max(100).nullable(),
})
    .strict()
    .refine((v) => (v.result !== null && v.result !== undefined) !== (v.error !== null));
export function isNyxRead(operation) {
    return ['status', 'projects', 'history', 'summary', 'models', 'tasks', 'events'].includes(operation.method);
}
export function freshRequest(request, now = Date.now()) {
    return (request.issuedAt <= now + 30000 &&
        request.issuedAt + 300000 >= now &&
        new TextEncoder().encode(JSON.stringify(request)).length <= NYX_MAX_BYTES);
}
export const statusSchema = z.object({
    protocolVersion: z.literal(1),
    version: z.string(),
    taskLimit: z.number(),
    clients: z
        .array(z.object({ client: clientSchema, installed: z.boolean(), modes: z.array(modeSchema) }))
        .max(3),
});
export const projectsSchema = z.object({
    projects: z
        .array(z.object({ projectKey: project, name: z.string().max(500), mapped: z.boolean() }))
        .max(40),
    cloudAvailable: z.boolean(),
    truncated: z.boolean(),
});
export const modelsSchema = z.object({
    models: z
        .array(z.object({
        id: model,
        name: z.string().max(500),
        efforts: z.array(z.string().max(20)).max(12),
    }))
        .max(100),
});
export const taskSchema = z.object({
    id,
    projectKey: project,
    client: clientSchema,
    mode: modeSchema,
    model: z.string().nullable(),
    effort: z.string().nullable(),
    state: z.enum(['idle', 'running', 'waiting', 'ended', 'interrupted', 'failed']),
    createdAt: z.number(),
    updatedAt: z.number(),
    remote: z.boolean(),
});
export const tasksSchema = z.object({ tasks: z.array(taskSchema).max(20), truncated: z.boolean() });
export const questionSchema = z.object({
    id: text(100),
    prompt: z.string().max(2000),
    allowMultiple: z.boolean(),
    options: z
        .array(z.object({ id: text(100), label: z.string().max(500) }))
        .min(1)
        .max(8),
});
export const eventSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('message'), speaker: z.enum(['user', 'agent']), text: z.string() }),
    z.object({ kind: z.literal('turn_started'), turnId: z.string().nullable() }),
    z.object({
        kind: z.literal('turn_finished'),
        turnId: z.string().nullable(),
        error: z.string().nullable(),
    }),
    z.object({
        kind: z.literal('tool_call'),
        callId: z.string().nullable(),
        name: z.string(),
        detail: z.string(),
    }),
    z.object({
        kind: z.literal('tool_result'),
        callId: z.string().nullable(),
        name: z.string(),
        detail: z.string(),
        failed: z.boolean(),
    }),
    z.object({
        kind: z.literal('approval'),
        requestId: text(200),
        action: z.enum(['command', 'file_change', 'permission', 'other']),
        summary: z.string(),
    }),
    z.object({
        kind: z.literal('question'),
        requestId: text(200),
        questions: z.array(questionSchema).min(1).max(4),
    }),
    z.object({ kind: z.literal('answered'), requestId: text(200), approved: z.boolean().nullable() }),
    z.object({ kind: z.literal('rate_limit'), detail: z.string() }),
    z.object({ kind: z.literal('failed'), code: z.string() }),
]);
export const eventsSchema = z.object({
    task: taskSchema,
    events: z.array(z.object({ sequence: z.number().int().positive(), event: eventSchema })).max(512),
    nextCursor: z.number().int().nonnegative(),
    hasMore: z.boolean(),
    truncated: z.boolean(),
    historyAvailable: z.boolean(),
    pending: z.array(text(200)).max(32),
});
export const historySchema = z.object({
    sessions: z
        .array(z.object({
        sessionId: text(200),
        title: z.string().nullable(),
        client: z.string(),
        status: z.string(),
        model: z.string().nullable(),
        updatedAt: z.string(),
        workItems: z.array(z.string()),
    }))
        .max(10),
    nextCursor: z.string().nullable(),
    paged: z.boolean(),
});
export const summarySchema = z.object({
    sessionId: z.string(),
    summary: z
        .object({
        text: z.string(),
        goal: z.string().nullable(),
        nextActions: z.array(z.string()),
        blockers: z.array(z.string()),
    })
        .nullable(),
    workItems: z.array(z.object({ key: z.string(), title: z.string().nullable(), status: z.string().nullable() })),
    truncated: z.boolean(),
});
//# sourceMappingURL=index.js.map